import { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../i18n';
import TranslatedText from './TranslatedText';
import axios from 'axios';
import { 
  Megaphone, FileText, Image, File, Download, ExternalLink,
  Calendar, Tag, ChevronDown, ChevronUp, Bell
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = {
  generale: { label: 'Generale', color: 'cyan', icon: Megaphone },
  protocollo: { label: 'Protocollo', color: 'blue', icon: FileText },
  evento: { label: 'Evento', color: 'violet', icon: Calendar },
  emergenza: { label: 'Emergenza', color: 'red', icon: Bell }
};

const AnnouncementSection = () => {
  const { language } = useLanguage();
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [filter, setFilter] = useState('all');

  const fetchAnnouncements = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/announcements`);
      setAnnouncements(res.data.announcements || []);
    } catch (error) {
      console.error('Error fetching announcements:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAnnouncements();
  }, [fetchAnnouncements]);

  const toggleExpand = (id) => {
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const getFileIcon = (type) => {
    switch (type) {
      case 'image': return <Image className="w-4 h-4" />;
      case 'pdf': return <FileText className="w-4 h-4" />;
      default: return <File className="w-4 h-4" />;
    }
  };

  const getCategoryInfo = (cat) => {
    return CATEGORIES[cat] || CATEGORIES.generale;
  };

  const filteredAnnouncements = filter === 'all' 
    ? announcements 
    : announcements.filter(a => a.category === filter);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-4 pb-24">
      {/* Header */}
      <div className="text-center mb-6">
        <div className="w-20 h-20 mx-auto rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center mb-4 shadow-lg shadow-violet-500/30">
          <Megaphone className="w-10 h-10 text-white" />
        </div>
        <h2 className="text-3xl font-bold text-white" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>
          <TranslatedText text="COMUNICAZIONI" />
        </h2>
        <p className="text-violet-400 mt-1">
          <TranslatedText text="Aggiornamenti e Protocolli" />
        </p>
      </div>

      {/* Filtri */}
      <div className="flex flex-wrap gap-2 mb-6 justify-center">
        <button
          onClick={() => setFilter('all')}
          className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
            filter === 'all' 
              ? 'bg-violet-500 text-white' 
              : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
          }`}
        >
          Tutti
        </button>
        {Object.entries(CATEGORIES).map(([key, val]) => (
          <button
            key={key}
            onClick={() => setFilter(key)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
              filter === key 
                ? `bg-${val.color}-500 text-white` 
                : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
            }`}
          >
            {val.label}
          </button>
        ))}
      </div>

      {/* Lista Annunci */}
      {filteredAnnouncements.length === 0 ? (
        <div className="text-center py-12 text-slate-400">
          <Megaphone className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p><TranslatedText text="Nessuna comunicazione disponibile" /></p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredAnnouncements.map((announcement) => {
            const catInfo = getCategoryInfo(announcement.category);
            const CatIcon = catInfo.icon;
            const isExpanded = expanded[announcement.id];

            return (
              <div 
                key={announcement.id} 
                className={`bg-slate-900/60 border border-${catInfo.color}-500/30 rounded-xl overflow-hidden`}
              >
                <div 
                  className="p-4 cursor-pointer hover:bg-slate-800/50 transition-all"
                  onClick={() => toggleExpand(announcement.id)}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-12 h-12 rounded-xl bg-${catInfo.color}-500/20 flex items-center justify-center flex-shrink-0`}>
                      <CatIcon className={`w-6 h-6 text-${catInfo.color}-400`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`px-2 py-0.5 text-xs rounded-full bg-${catInfo.color}-500/20 text-${catInfo.color}-400`}>
                          {catInfo.label}
                        </span>
                        <span className="text-xs text-slate-500">
                          {new Date(announcement.created_at).toLocaleDateString('it-IT', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </span>
                      </div>
                      <h3 className="text-white font-bold text-lg">{announcement.title}</h3>
                      {!isExpanded && (
                        <p className="text-slate-400 text-sm mt-1 line-clamp-2">
                          {announcement.body}
                        </p>
                      )}
                      {announcement.attachments?.length > 0 && !isExpanded && (
                        <p className="text-violet-400 text-xs mt-2 flex items-center gap-1">
                          <FileText className="w-3 h-3" />
                          {announcement.attachments.length} allegat{announcement.attachments.length > 1 ? 'i' : 'o'}
                        </p>
                      )}
                    </div>
                    <div className="flex-shrink-0">
                      {isExpanded ? (
                        <ChevronUp className="w-5 h-5 text-slate-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-slate-400" />
                      )}
                    </div>
                  </div>
                </div>

                {/* Contenuto espanso */}
                {isExpanded && (
                  <div className="px-4 pb-4 border-t border-slate-700/50">
                    <div className="pt-4">
                      <p className="text-slate-300 whitespace-pre-wrap leading-relaxed">
                        {announcement.body}
                      </p>

                      {/* Allegati */}
                      {announcement.attachments?.length > 0 && (
                        <div className="mt-4 pt-4 border-t border-slate-700/50">
                          <p className="text-xs text-slate-400 uppercase mb-3 flex items-center gap-1">
                            <Download className="w-3 h-3" />
                            Allegati
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {announcement.attachments.map((att, idx) => (
                              <a
                                key={idx}
                                href={att.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 rounded-lg text-white transition-all group"
                              >
                                {getFileIcon(att.type)}
                                <span className="text-sm">{att.name}</span>
                                <ExternalLink className="w-3 h-3 text-slate-400 group-hover:text-cyan-400" />
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AnnouncementSection;
